import "@fontsource-variable/inter/wght.css";
import "@fontsource-variable/geist-mono/wght.css";
import "./styles.css";
import { type AuthClient, ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { TooltipProvider } from "@buddy/ui";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { authClient } from "./lib/auth-client";
import { convex } from "./lib/convex";
import { routeTree } from "./routeTree.gen";

// A tab opened before a deploy asks for chunks the new build no longer has (they 404,
// worker/index.ts). Reload onto the current build instead of breaking mid page. At most
// once a minute, so a chunk missing for another reason cannot loop the page.
const RELOADED_AT = "buddy:reloaded-for-chunk";
window.addEventListener("vite:preloadError", (event) => {
  try {
    if (Date.now() - Number(sessionStorage.getItem(RELOADED_AT) ?? 0) < 60_000) return;
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

const router = createRouter({ routeTree, defaultPreload: "intent", scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// The provider types its client without the organization plugin, whose extra session fields
// make the inferred useSession type diverge. It only reads the session and the Convex token.
const providerClient = authClient as unknown as AuthClient;

const root = document.getElementById("root");
if (root === null) throw new Error("index.html is missing #root");

createRoot(root).render(
  <StrictMode>
    <ConvexBetterAuthProvider client={convex} authClient={providerClient}>
      <TooltipProvider delay={400}>
        <RouterProvider router={router} />
      </TooltipProvider>
    </ConvexBetterAuthProvider>
  </StrictMode>,
);
